import { useState } from "react";
import { toast } from "sonner";
import { Gift, Tag, Truck, Store, Users, X } from "lucide-react";
import { eur } from "../../lib/shield";
import { api, errMsg } from "../../lib/api";

export const COLLECT_ADDRESS = "Business Print, Unit 3B Ballymount Drive, Ballymount Road, Walkinstown, Dublin 12, D12 W028";

export const DiscountBox = ({ coupon, onChange }) => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const apply = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try { const { data } = await api.post("/coupons/check", { code }); onChange(data); toast.success(`${data.percent}% off applied`); setCode(""); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (coupon) return (
    <div className="flex items-center justify-between border-2 border-lime-500 bg-lime-50 px-3 py-2 text-sm font-bold" data-testid="discount-applied">
      <span className="flex items-center gap-2"><Tag className="h-4 w-4" /> {coupon.code} · {coupon.percent}% off shields</span>
      <button type="button" onClick={() => onChange(null)} aria-label="Remove discount code" data-testid="discount-remove-button"><X className="h-4 w-4" /></button>
    </div>
  );
  return (
    <div className="flex gap-2" data-testid="discount-box">
      <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), apply())} placeholder="Discount code"
        className="min-w-0 flex-1 border-2 border-slate-200 px-3 py-2 text-sm font-semibold uppercase outline-none focus:border-slate-900" data-testid="discount-code-input" aria-label="Discount code" />
      <button type="button" onClick={apply} disabled={busy || !code.trim()} className="border-2 border-slate-900 px-4 text-xs font-bold uppercase hover:bg-slate-100 disabled:opacity-50" data-testid="discount-apply-button">{busy ? "…" : "Apply"}</button>
    </div>
  );
};

export const VoucherBox = ({ voucher, onChange, email }) => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const apply = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try { const { data } = await api.post("/vouchers/check", { code, email: email || null }); onChange(data); toast.success(`Gift voucher applied – ${eur(data.balance)} available`); setCode(""); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (voucher) return (
    <div className="flex items-center justify-between border-2 border-red-400 bg-red-50 px-3 py-2 text-sm font-bold" data-testid="voucher-applied">
      <span className="flex items-center gap-2"><Gift className="h-4 w-4 text-red-600" /> {voucher.code} · {eur(voucher.balance)} available</span>
      <button type="button" onClick={() => onChange(null)} aria-label="Remove gift voucher" data-testid="voucher-remove-button"><X className="h-4 w-4" /></button>
    </div>
  );
  return (
    <div className="flex gap-2" data-testid="voucher-box">
      <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), apply())} placeholder="Gift voucher code"
        className="min-w-0 flex-1 border-2 border-slate-200 px-3 py-2 text-sm font-semibold uppercase outline-none focus:border-slate-900" data-testid="voucher-code-input" aria-label="Gift voucher code" />
      <button type="button" onClick={apply} disabled={busy || !code.trim()} className="border-2 border-slate-900 px-4 text-xs font-bold uppercase hover:bg-slate-100 disabled:opacity-50" data-testid="voucher-apply-button">{busy ? "…" : "Apply"}</button>
    </div>
  );
};

export const FulfilmentChoice = ({ value, onChange, deliveryLabel, club }) => {
  const opts = [["deliver", Truck, "Delivery to my home", deliveryLabel], club
    ? ["club", Users, "Delivery to my club – free of charge", "Delivered to your coach or club with the squad when the fundraiser closes"]
    : ["collect", Store, "Click & collect – free", COLLECT_ADDRESS]];
  return (
    <div className="space-y-2" role="radiogroup" data-testid="fulfilment-choice">
      <div className="font-display text-lg font-extrabold uppercase">Delivery or collection</div>
      {opts.map(([v, Icon, t, d]) => (
        <button key={v} type="button" role="radio" aria-checked={value === v} onClick={() => onChange(v)} data-testid={`fulfilment-${v}`}
          className={`flex w-full items-start gap-3 border-2 p-3 text-left text-sm ${value === v ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-400"}`}>
          <Icon className="mt-0.5 h-5 w-5 shrink-0" /><span><span className="block font-bold">{t}</span><span className="text-xs text-slate-600">{d}</span></span>
        </button>
      ))}
    </div>
  );
};
